import { adminSilentApi, userSilentApi } from "../../utils/apiInstances";
import { ApiResponse } from "../types";

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue>;

/**
 * Admin-only: Get statistics.
 */
export const handleGetAdminStats = async (params: QueryParams = {}) => {
    try {
        const queryParams = new URLSearchParams(
            Object.entries(params)
                .filter(([, value]) => value !== undefined && value !== null && value !== "")
                .map(([key, value]) => [key, String(value)])
        ).toString();
        const response = await adminSilentApi("GET", `/admin/admin-statistics${queryParams ? `?${queryParams}` : ""}`);
        return response;
    } catch (error) {
        console.error("Error fetching admin stats:", error);
        throw error;
    }
};

/**
 * Admin-only: Get analytics data (revenue, users, games).
 */
export const handleGetAdminAnalytics = async (
    type: "revenue" | "users" | "games",
    params: QueryParams = {}
): Promise<ApiResponse> => {
    try {
        const queryParams = new URLSearchParams(
            Object.entries(params)
                .filter(([, value]) => value !== undefined && value !== null)
                .map(([key, value]) => [key, String(value)])
        ).toString();
        const response = await adminSilentApi("GET", `/admin/admin-analytics/${type}${queryParams ? `?${queryParams}` : ""}`);
        return response;
    } catch (error) {
        console.error(`Error fetching admin ${type} analytics:`, error);
        throw error;
    }
};
/**
 * Get plans (context-aware).
 */
export const handleGetPlans = async <TData = unknown>(context: "admin" | "user" = "user"): Promise<ApiResponse<TData>> => {
    try {
        const api = context === "admin" ? adminSilentApi : userSilentApi;
        const endpoint = context === "admin" ? "/admin/admin-plans" : "/user/plans";
        const response = await api<TData>("GET", endpoint);
        return response;
    } catch (error) {
        console.error(`Error fetching plans (${context}):`, error);
        throw error;
    }
};

/**
 * User-only: Get countries.
 */
export const handleGetCountries = async (params: QueryParams = {}): Promise<ApiResponse> => {
    try {
        const queryParams = new URLSearchParams();
        if (params.active !== undefined) queryParams.append("active", params.active ? "1" : "0");
        if (params.with_states !== undefined) queryParams.append("with_states", params.with_states ? "1" : "0");
        if (params.with_cities !== undefined) queryParams.append("with_cities", params.with_cities ? "1" : "0");
        if (params.search !== undefined && params.search !== null && params.search !== "") queryParams.append("q", String(params.search));
        // Include any other params
        Object.keys(params).forEach(key => {
            if (!['active', 'with_states', 'with_cities', 'search'].includes(key)) {
                queryParams.append(key, String(params[key]));
            }
        });

        const queryString = queryParams.toString();
        const response = await userSilentApi("GET", `/user/countries${queryString ? `?${queryString}` : ""}`) as ApiResponse;
        return response;
    } catch (error) {
        console.error("Error fetching countries:", error);
        throw error;
    }
};

/**
 * User-only: Get states by country ID.
 */
export const handleGetStatesByCountry = async (
    countryId: string | number,
    params: QueryParams = {}
): Promise<ApiResponse | null> => {
    if (!countryId) return null;
    try {
        const queryParams = new URLSearchParams({ country_id: String(countryId) });
        if (params.active !== undefined) queryParams.append("active", params.active ? "1" : "0");
        if (params.with_cities !== undefined) queryParams.append("with_cities", params.with_cities ? "1" : "0");
        if (params.search !== undefined && params.search !== null && params.search !== "") queryParams.append("q", String(params.search));
        // Include any other params
        Object.keys(params).forEach(key => {
            if (!['active', 'with_cities', 'search'].includes(key)) {
                queryParams.append(key, String(params[key]));
            }
        });

        const response = await userSilentApi("GET", `/user/states?${queryParams.toString()}`) as ApiResponse;
        return response;
    } catch (error) {
        console.error("Error fetching states:", error);
        throw error;
    }
};

/**
 * User-only: Get cities by state ID.
 */
export const handleGetCitiesByState = async (
    stateId: string | number,
    params: QueryParams = {}
): Promise<ApiResponse | null> => {
    if (!stateId) return null;
    try {
        const queryParams = new URLSearchParams({ state_id: String(stateId) });
        if (params.active) queryParams.append("active", "1");
        if (params.search) queryParams.append("q", String(params.search));

        const response = await userSilentApi("GET", `/user/cities?${queryParams.toString()}`) as ApiResponse;
        return response;
    } catch (error) {
        console.error("Error fetching cities:", error);
        throw error;
    }
};

/**
 * User-only: Create a new city.
 */
export const handleCreateCity = async (params: { stateId: string | number; name: string }) => {
    if (!params.stateId || !params.name) throw new Error("State and city name are required");
    try {
        const response = await userSilentApi("POST", "/user/cities", { state_id: params.stateId, name: params.name });
        return response;
    } catch (error) {
        console.error("Error creating city:", error);
        throw error;
    }
};

/**
 * User-only: Get legacy bookings (games).
 */
export const handleGetUserBookings = async <TData = unknown>(): Promise<ApiResponse<TData>> => {
    try {
        const response = await userSilentApi<TData>("GET", "/user/booked-games");
        return response;
    } catch (error) {
        console.error("Error fetching user bookings:", error);
        throw error;
    }
};

/**
 * User-only: Get aggregated appointments (Game + Pitch).
 */
export const handleGetUserAppointments = async <TData = unknown>(
    params: QueryParams = {}
): Promise<ApiResponse<TData>> => {
    try {
        const normalizedStatus = typeof params.status === 'string'
            ? params.status.trim().toLowerCase()
            : null;

        const defaultParams: QueryParams = {
            per_page: typeof params.per_page === "number" ? params.per_page : 10
        };

        if (params.time === undefined && normalizedStatus !== 'booked') {
            defaultParams.time = 'upcoming';
        }

        const queryParams = { ...defaultParams, ...params };
        const queryString = Object.keys(queryParams)
            .filter((key) => queryParams[key] !== undefined && queryParams[key] !== null && queryParams[key] !== "")
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(queryParams[key]))}`)
            .join("&");

        const response = await userSilentApi<TData>("GET", `/user/appointments${queryString ? `?${queryString}` : ""}`);
        return response;
    } catch (error) {
        console.error("Error fetching user appointments:", error);
        throw error;
    }
};
